import React, { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import SwalAlert from "../../components/common/alert";
import FileUploadGrid from "../../components/upload-file";
import {
  callCreateLegacyLifeBill,
  callGetLegacyLifeBillRequestById,
  callUpdateRejectedLegacyLifeBill,
  callPreviewLegacyLifeBill,
  callSearchLegacyLifeContract,
  callUploadLegacyLifeEntryFiles,
} from "../../api/legacy-life-entry/service";
import "./legacy-life-entry.css";
import "./legacy-life-entry-v6.css";
import { dateToString } from "../../utils/commonUtil";

const toDate = value => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const parsed = moment(value, [moment.ISO_8601, "YYYY-MM-DD", "DD/MM/YYYY"], true);
  return parsed.isValid() ? parsed.toDate() : null;
};
const dateValue = date => {
  if (!date) return "";
  const parsed = moment(date);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
};
const textValue = value => ["string", "number"].includes(typeof value) ? String(value) : "";
const PROMOTIONS = {
  NONE: 'NONE',
  ENTRY_FREE_FIRST_MONTH_REGISTRATION: 'ENTRY_FREE_FIRST_MONTH_REGISTRATION',
  PAY_12_FREE_1: 'PAY_12_FREE_1',
};

const normalizeRows = value => {
  if (Array.isArray(value)) return value;
  for (const key of ["data", "result", "rows", "items"]) {
    if (Array.isArray(value?.[key])) return value[key];
  }
  return [];
};

export default function LegacyLifeBillEntry() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [rejectReason, setRejectReason] = useState("");
  const fromList = Boolean(location.state?.fromList);
  const [keyword, setKeyword] = useState("");
  const [contracts, setContracts] = useState([]);
  const [contract, setContract] = useState(null);
  const [bill, setBill] = useState({ billNumber: "", paidDate: "", monthCount: "", promotionCode: PROMOTIONS.NONE, note: "" });
  const [periods, setPeriods] = useState([]);
  const [files, setFiles] = useState([]);
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [errors, setErrors] = useState({});
  const [searching, setSearching] = useState(false);


  useEffect(() => {
    if (!id) return;
    dispatch(callGetLegacyLifeBillRequestById(id)).then(rsp => {
      if (rsp?.payload?.responseCode !== "00000") return;
      const row = rsp.payload.data || {};
      if (row.status !== "REJECTED") return;
      const contractNumber = row.contractNumber || row.contract_number || "";
      setContract({ contractNumber, customerName: row.customerName, agentId: row.agentId, agentCode: row.agentCode, payPerMonth: row.periods?.[0]?.payPerMonth || 0, hasPreviousPayment: row.previousPaidDate ? 1 : 0 });
      setKeyword(contractNumber);
      setBill({ billNumber: row.billNumber || row.bill_number || "", paidDate: row.paidDate || row.paid_date || "", monthCount: String(row.monthCount ?? row.month_count ?? ""), promotionCode: row.promotionCode || row.promotion_code || PROMOTIONS.NONE, note: row.note || "" });
      setPeriods(Array.isArray(row.periods) ? row.periods : []);
      setRejectReason(row.rejectReason || row.reject_reason || "");
      setExistingDocuments(Array.isArray(row.documents) ? row.documents : []);
    });
  }, [dispatch, id]);

  const searchContracts = async () => {
    const value = keyword.trim();
    if (!value) return setErrors(current => ({ ...current, contract: true }));
    setSearching(true);
    try {
      const rsp = await dispatch(callSearchLegacyLifeContract({ keyword: value }));
      setContracts(rsp?.payload?.responseCode === "00000" ? normalizeRows(rsp.payload.data) : []);
    } finally {
      setSearching(false);
    }
  };

  const selectContract = item => {
    if (!item?.contractNumber) return;
    setContract(item);
    setBill(current => ({
      ...current,
      promotionCode: Number(item.hasPreviousPayment) === 1 ? PROMOTIONS.NONE : current.promotionCode,
    }));
    setKeyword(textValue(item.contractNumber));
    setContracts([]);
    setPeriods([]);
    setErrors(current => ({ ...current, contract: false }));
  };

  const preview = async () => {
    const next = {};
    if (!contract?.contractNumber) next.contract = true;
    if (!Number.isInteger(Number(bill.monthCount)) || Number(bill.monthCount) < 1) next.monthCount = true;
    setErrors(next);
    if (Object.keys(next).length) return;
    const rsp = await dispatch(callPreviewLegacyLifeBill({
      contractNumber: contract.contractNumber,
      monthCount: Number(bill.monthCount),
      promotionCode: bill.promotionCode,
    }));
    setPeriods(rsp?.payload?.responseCode === "00000" ? normalizeRows(rsp?.payload?.data?.periods) : []);
  };

  const total = useMemo(() => {
    const gross = periods.reduce((sum, item) => sum + Number(item?.payPerMonth || 0), 0);
    const discount = bill.promotionCode === PROMOTIONS.NONE ? 0 : Number(contract?.payPerMonth || 0);
    return Math.max(0, gross - discount);
  }, [periods, bill.promotionCode, contract]);

  const submitForApproval = async () => {
    const next = {};
    if (!contract?.contractNumber) next.contract = true;
    if (!bill.billNumber.trim()) next.billNumber = true;
    if (!bill.paidDate) next.paidDate = true;
    if (!Number.isInteger(Number(bill.monthCount)) || Number(bill.monthCount) < 1) next.monthCount = true;
    setErrors(next);
    if (Object.keys(next).length) {
      return SwalAlert.error(t("legacyLifeEntry.validationTitle"), t("legacyLifeEntry.validationMessage"));
    }

    let documents = [...existingDocuments];
    if (files.length) {
      const upload = await dispatch(callUploadLegacyLifeEntryFiles({ contractNumber: bill.billNumber, files }));
      if (upload?.payload?.responseCode !== "00000") return;
      documents = [...documents, ...normalizeRows(upload?.payload?.data)];
    }

    const requestData = {
      contractNumber: contract.contractNumber, billNumber: bill.billNumber.trim(), paidDate: bill.paidDate,
      monthCount: Number(bill.monthCount), promotionCode: bill.promotionCode, documents, note: bill.note,
    };
    const rsp = await dispatch(id ? callUpdateRejectedLegacyLifeBill({ id, data: requestData }) : callCreateLegacyLifeBill(requestData));

    if (rsp?.payload?.responseCode === "00000") {
      SwalAlert.successMSG(t("legacyLifeEntry.billPendingApproval"));
      // Reset the whole bill-entry form so the operator can immediately key a new bill.
      setKeyword("");
      setContracts([]);
      setContract(null);
      setBill({ billNumber: "", paidDate: "", monthCount: "", promotionCode: PROMOTIONS.NONE, note: "" });
      setPeriods([]);
      setFiles([]);
      setExistingDocuments([]);
      setErrors({});
      setSearching(false);
      if (fromList) navigate("/admin/legacyLifeEntry/list", { replace: true });
    }
  };

  return (
    <div className="legacy-wrap legacy-modern">
      <div className="legacy-head legacy-bill-head">
        <div className="legacy-head-with-back">
          {fromList && <button type="button" className="legacy-back-button" onClick={() => navigate("/admin/legacyLifeEntry/list")}>← {t("legacyLifeEntry.back")}</button>}
          <div>
            <h3>{t("legacyLifeEntry.billEntryTitle")}</h3>
            <p>{t("legacyLifeEntry.billApprovalHint")}</p>
          </div>
        </div>
        <span className="legacy-badge PENDING">{t("legacyLifeEntry.statusText.PENDING")}</span>
      </div>

      {id && rejectReason && <div className="legacy-reject-notice"><div><strong>{t("legacyLifeEntry.rejectedComment")}</strong><p>{rejectReason}</p></div></div>}

      <section className="legacy-card legacy-contract-finder-card">
        <div className="legacy-card-title legacy-finder-title">
          <div><span>01</span><h4>{t("legacyLifeEntry.contractAndInsured")}</h4></div>
          <small>{t("legacyLifeEntry.contractSearchHint")}</small>
        </div>
        <div className="legacy-contract-finder">
          <div className="legacy-finder-icon">⌕</div>
          <div className={`legacy-search-input legacy-search-input-modern${errors.contract ? " is-invalid" : ""}`}>
            <input value={keyword} onChange={event => { setKeyword(event.target.value); setContract(null); setPeriods([]); }} onKeyDown={event => event.key === "Enter" && searchContracts()} placeholder={t("legacyLifeEntry.contractSearchPlaceholder")} />
            {keyword && <button type="button" className="legacy-search-clear" onClick={() => { setKeyword(""); setContract(null); setContracts([]); setPeriods([]); }}>×</button>}
            <button type="button" className="legacy-search-submit" onClick={searchContracts}>{searching ? "..." : t("legacyLifeEntry.searchButton")}</button>
          </div>
          {contracts.length > 0 && <div className="legacy-search-results legacy-search-results-modern">{contracts.map((item, index) => (
            <button type="button" key={`${item.contractNumber}-${index}`} onClick={() => selectContract(item)}>
              <span className="legacy-result-avatar">{textValue(item.customerName).slice(0, 1) || "#"}</span>
              <div className="legacy-result-main"><b>{textValue(item.contractNumber)}</b><small>{textValue(item.customerName)}</small></div>
              <div className="legacy-result-meta"><small>{t("legacyLifeEntry.premiumPerMonth")}</small><strong>{Number(item.payPerMonth || 0).toLocaleString()} LAK</strong></div>
            </button>
          ))}</div>}
        </div>
        {contract && <div className="legacy-contract-summary legacy-contract-summary-modern">
          <div className="legacy-summary-primary"><span>{t("legacyLifeEntry.contractNumber")}</span><b>{contract.contractNumber}</b><small>{contract.customerName || "-"}</small></div>
          <div><span>{t("legacyLifeEntry.entryDate")}</span><b>{dateToString(textValue(contract.entryDate)) || "-"}</b></div>
          <div><span>{t("legacyLifeEntry.premiumPerMonth")}</span><b>{Number(contract.payPerMonth || 0).toLocaleString()} LAK</b></div>
          <div><span>{t("legacyLifeEntry.lastPaidPeriod")}</span><b>{contract.lastPay || "-"}</b></div>
          <div><span>{t("contracts.life.agentCode")}</span><b>{textValue(contract.agentCode || contract.agentId) || "-"}</b></div>
        </div>}
      </section>

      <section className="legacy-card">
        <div className="legacy-card-title"><div><span>02</span><h4>{t("legacyLifeEntry.billInfo")}</h4></div></div>
        <div className="legacy-form-grid">
          <div className="legacy-field"><label>{t("legacyLifeEntry.billNumber")} *</label><input className={`form-control-uld${errors.billNumber ? " is-invalid" : ""}`} value={bill.billNumber} onChange={event => setBill(current => ({ ...current, billNumber: event.target.value }))} /></div>
          <div className="legacy-field"><label>{t("legacyLifeEntry.paidDate")} *</label><DatePicker selected={toDate(bill.paidDate)} onChange={date => setBill(current => ({ ...current, paidDate: dateValue(date) }))} dateFormat="dd/MM/yyyy" className={`form-control-uld${errors.paidDate ? " is-invalid" : ""}`} /></div>
          <div className="legacy-field"><label>{t("legacyLifeEntry.monthCount")} *</label><input type="number" min="1" max="240" className={`form-control-uld${errors.monthCount ? " is-invalid" : ""}`} value={bill.monthCount} onChange={event => { setBill(current => ({ ...current, monthCount: event.target.value })); setPeriods([]); }} /></div>
          <div className="legacy-field legacy-span3"><label>{t("legacyLifeEntry.remark")}</label><textarea className="form-control-uld" rows="2" value={bill.note} onChange={event => setBill(current => ({ ...current, note: event.target.value }))} /></div>
        </div>
        <div className="legacy-promotion-block">
          <div className="legacy-promotion-heading"><div><b>{t("legacyLifeEntry.promotionTitle")}</b><small>{t("legacyLifeEntry.promotionHint")}</small></div><span>{t("legacyLifeEntry.discountFromUi")}</span></div>
          <div className="legacy-promotion-grid">
            <button type="button" className={`legacy-promo-option${bill.promotionCode === PROMOTIONS.NONE ? " active" : ""}`} onClick={() => { setBill(current => ({ ...current, promotionCode: PROMOTIONS.NONE })); setPeriods([]); }}>
              <span className="legacy-promo-radio" /><div><b>{t("legacyLifeEntry.promoNone")}</b><small>{t("legacyLifeEntry.promoNoneDesc")}</small></div>
            </button>
            <button type="button" disabled={Number(contract?.hasPreviousPayment) === 1} className={`legacy-promo-option${bill.promotionCode === PROMOTIONS.ENTRY_FREE_FIRST_MONTH_REGISTRATION ? " active" : ""}${Number(contract?.hasPreviousPayment) === 1 ? " disabled" : ""}`} onClick={() => { if (Number(contract?.hasPreviousPayment) === 1) return; setBill(current => ({ ...current, promotionCode: PROMOTIONS.ENTRY_FREE_FIRST_MONTH_REGISTRATION })); setPeriods([]); }}>
              <span className="legacy-promo-radio" /><div><b>{t("legacyLifeEntry.promoEntryFree")}</b><small>{Number(contract?.hasPreviousPayment) === 1 ? t("legacyLifeEntry.promoEntryAlreadyPaid") : t("legacyLifeEntry.promoEntryFreeDesc")}</small></div><strong>-{Number(contract?.payPerMonth || 0).toLocaleString()} LAK</strong>
            </button>
            <button type="button" className={`legacy-promo-option${bill.promotionCode === PROMOTIONS.PAY_12_FREE_1 ? " active" : ""}`} onClick={() => { setBill(current => ({ ...current, promotionCode: PROMOTIONS.PAY_12_FREE_1 })); if (Number(bill.monthCount || 0) < 12) setBill(current => ({ ...current, monthCount: 12, promotionCode: PROMOTIONS.PAY_12_FREE_1 })); setPeriods([]); }}>
              <span className="legacy-promo-radio" /><div><b>{t("legacyLifeEntry.promo12Free1")}</b><small>{t("legacyLifeEntry.promo12Free1Desc")}</small></div><strong>{t("legacyLifeEntry.promoMonth12Free")}</strong>
            </button>
          </div>
        </div>
        <div className="legacy-period-preview">
          <button type="button" className="legacy-btn-secondary" onClick={preview}>{t("legacyLifeEntry.calculatePeriods")}</button>
          <div>{periods.map((period, index) => <span key={`${period.paidYear}-${period.paidMonth}-${index}`}>{String(period.paidMonth).padStart(2, "0")}/{period.paidYear}</span>)}</div>
          <strong>{total.toLocaleString()} LAK</strong>
        </div>
      </section>

      <section className="legacy-card">
        <div className="legacy-card-title"><div><span>03</span><h4>{t("legacyLifeEntry.billDocuments")}</h4></div></div>
        <FileUploadGrid multiple={false} accept="image/*,.pdf" valueFile={files} onChange={value => setFiles(Array.isArray(value?.files) ? value.files : [])} folderName="legacy_life_bill" referenceName={bill.billNumber || "draft_bill"} />
      </section>

      <div className="legacy-sticky-actions">
        <div><b>{t("legacyLifeEntry.approvalRequired")}</b><small>{t("legacyLifeEntry.billApprovalHint")}</small></div>
        <button type="button" className="legacy-btn-primary" onClick={submitForApproval}>{t("legacyLifeEntry.submitForApproval")}</button>
      </div>
    </div>
  );
}
